import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ChevronDown, Menu, X, ArrowUpRight } from 'lucide-react';
import { NAV, NAV_SECONDARY, NAV_HOMEOWNER, SITE, BRAND } from '@/lib/site';
import { trackEvent, EVENTS } from '@/lib/analytics';

export function Wordmark({ dark = false, className = '' }) {
  return (
    <Link to="/" className={`flex items-center gap-2.5 ${className}`} data-testid="brand-logo" aria-label="HyenaMind home">
      <picture>
        <source srcSet={BRAND.emblemSmallWebp} type="image/webp" />
        <img src={BRAND.emblemSmall} alt="HyenaMind logo" className="h-9 w-auto" width="38" height="45" />
      </picture>
      <span className={`font-display text-xl font-semibold tracking-tight ${dark ? 'text-bone' : 'text-navy'}`}>
        Hyena<span className="text-steel-light">Mind</span>
      </span>
    </Link>
  );
}

export default function Header() {
  const reduce = useReducedMotion();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setServicesOpen(null);
    setMenuOpen(null);
  }, [location.pathname]);

  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e) => {
      if (!e.target.closest('[data-nav-dropdown]')) setMenuOpen(null);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setMenuOpen(null);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-bone/90 backdrop-blur-md border-b border-navy/10 py-2' : 'bg-bone/75 backdrop-blur-sm py-3.5'
        }`}
      >
        <div className="container-site flex items-center justify-between gap-6">
          <Wordmark />
          <nav aria-label="Primary" className="hidden lg:flex items-center gap-5">
            {NAV.map((item) =>
              item.children || item.mega ? (
                <div key={item.label} className="relative group" data-nav-dropdown>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-[15px] font-semibold text-navy/80 hover:text-navy transition-colors py-2"
                    aria-haspopup="true"
                    aria-expanded={menuOpen === item.label}
                    onClick={() => setMenuOpen((v) => (v === item.label ? null : item.label))}
                    data-testid={`nav-${item.label.toLowerCase()}-toggle`}
                  >
                    {item.label}
                    <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 group-hover:rotate-180 ${menuOpen === item.label ? 'rotate-180' : ''}`} />
                  </button>
                  <div
                    className={`absolute left-0 top-full pt-2 transition-all duration-300 group-hover:visible group-hover:opacity-100 group-hover:translate-y-0 group-focus-within:visible group-focus-within:opacity-100 group-focus-within:translate-y-0 ${
                      menuOpen === item.label
                        ? 'visible opacity-100 translate-y-0'
                        : 'invisible opacity-0 translate-y-2'
                    }`}
                  >
                    {item.mega ? (
                      <div
                        className={`bg-bone border border-navy/12 rounded-md shadow-[0_18px_44px_-24px_rgba(10,17,40,0.45)] p-7 ${
                          item.cols === 3 ? 'grid-cols-3 w-[46rem]' : 'grid-cols-2 w-[34rem]'
                        } grid gap-x-9 gap-y-7`}
                      >
                        {item.mega.map((col) => (
                          <div key={col.title}>
                            <p className="border-t border-navy/20 pt-2.5 text-[10px] font-bold uppercase tracking-[0.2em] text-steel">
                              {col.title}
                            </p>
                            <div className="mt-2.5">
                              {col.links.map((c) => (
                                <NavLink
                                  key={c.to}
                                  to={c.to}
                                  className="block py-[5px] font-display text-[15px] font-medium tracking-tight text-navy/70 transition-colors hover:text-navy"
                                >
                                  {c.label}
                                </NavLink>
                              ))}
                            </div>
                          </div>
                        ))}
                        {item.footer && (
                          <NavLink
                            to={item.footer.to}
                            className={`group/f col-span-full flex items-center gap-2.5 border-t border-navy/12 pt-4 text-[13px] font-bold text-navy/60 transition-colors hover:text-steel ${
                              item.cols === 3 ? '' : ''
                            }`}
                          >
                            {item.footer.label}
                            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover/f:translate-x-0.5 group-hover/f:-translate-y-0.5" />
                          </NavLink>
                        )}
                      </div>
                    ) : (
                      <div className="w-[19rem] rounded-md border border-navy/12 bg-bone p-5 shadow-[0_18px_44px_-24px_rgba(10,17,40,0.45)]">
                        {item.children.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            className="block border-t border-navy/10 py-2.5 font-display text-[15px] font-medium tracking-tight text-navy/70 transition-colors first:border-t-0 hover:text-navy"
                          >
                            {c.label}
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={({ isActive }) =>
                    `text-[15px] font-semibold transition-colors py-2 ${isActive ? 'text-steel' : 'text-navy/80 hover:text-navy'}`
                  }
                >
                  {item.label}
                </NavLink>
              )
            )}
          </nav>
          <div className="hidden lg:flex items-center gap-4">
            <Link
              to="/contractors/grow-your-business"
              onClick={() => trackEvent(EVENTS.growthCta, { location: 'header' })}
              data-testid="header-cta-growth"
              className="btn-primary !px-6 !py-3 group"
            >
              Build My Growth System
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>
          <button
            className="lg:hidden p-2 -mr-2 text-navy"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={reduce ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-navy text-bone flex flex-col"
            data-testid="mobile-menu"
            role="dialog"
            aria-modal="true"
            aria-label="Site menu"
          >
            <div className="container-site flex items-center justify-between py-4 border-b border-white/10">
              <Wordmark dark />
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close" className="p-2 -mr-2">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav aria-label="Mobile" className="container-site flex-1 overflow-y-auto py-8 flex flex-col gap-1">
              {NAV.map((item, i) =>
                item.children || item.mega ? (
                  <div key={item.label}>
                    <button
                      onClick={() => setServicesOpen((v) => (v === item.label ? null : item.label))}
                      className="w-full flex items-center justify-between py-3 font-display text-2xl font-medium"
                      aria-expanded={servicesOpen === item.label}
                      data-testid={`mobile-nav-${item.label.toLowerCase()}-toggle`}
                    >
                      {item.label}
                      <ChevronDown className={`h-5 w-5 transition-transform duration-300 ${servicesOpen === item.label ? 'rotate-180' : ''}`} />
                    </button>
                    <AnimatePresence>
                      {servicesOpen === item.label && (
                        <motion.div
                          initial={reduce ? false : { height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="pl-4 pb-3 flex flex-col gap-1 border-l border-white/15 ml-1">
                            {(item.children || item.mega.flatMap((col) => col.links)).map((c) => (
                              <NavLink key={c.to} to={c.to} className="py-2 text-base font-medium text-silver hover:text-bone">
                                {c.label}
                              </NavLink>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <motion.div key={item.to} initial={reduce ? false : { opacity: 0, x: -12 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                    <NavLink to={item.to} className="block py-3 font-display text-2xl font-medium hover:text-steel-light transition-colors">
                      {item.label}
                    </NavLink>
                  </motion.div>
                )
              )}
              <div className="mt-7 border-t border-white/10 pt-6">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-steel-light">For homeowners</p>
                <div className="mt-3 flex flex-col">
                  {NAV_HOMEOWNER.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className="py-2.5 font-display text-lg font-medium text-silver transition-colors hover:text-bone"
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
              <div className="mt-6 flex flex-col border-t border-white/10 pt-6">
                {NAV_SECONDARY.map((item) => (
                  <NavLink key={item.to} to={item.to} className="py-2.5 font-display text-lg font-medium text-silver/80 transition-colors hover:text-bone">
                    {item.label}
                  </NavLink>
                ))}
              </div>
              <div className="mt-8 flex flex-col gap-3">
                <Link to="/contractors/grow-your-business" data-testid="mobile-cta-growth" className="btn-accent w-full">
                  Build My Growth System
                </Link>
                <Link to="/contractors/get-qualified-leads" data-testid="mobile-cta-leads" className="btn-ghost-light w-full">
                  Get Qualified Leads
                </Link>
                <Link to="/get-renovation-quotes" data-testid="mobile-cta-quote" className="btn-ghost-light w-full">
                  Get Renovation Quotes
                </Link>
              </div>
              <div className="mt-10 pt-6 border-t border-white/10 text-sm text-silver flex flex-col gap-2">
                <a href={SITE.phoneHref} className="font-bold text-bone" data-testid="mobile-phone-link">{SITE.phone}</a>
                <a href={`mailto:${SITE.email}`} data-testid="mobile-email-link">{SITE.email}</a>
                <span>{SITE.address}</span>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
